import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Button,
    TextField,
    Container,
    Paper,
    Dialog,
    DialogContent,
    DialogActions,
    InputAdornment,
    IconButton,
    Alert
} from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AuthService } from '@df/api';
import { getSafeApiErrorMessage, isNetworkError } from '../../utils/errorUtils';

const PasswordResetConfirm = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [formData, setFormData] = useState({
        new_password: '',
        new_password_confirm: ''
    });
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [modalOpen, setModalOpen] = useState(false);
    const [modalStatus, setModalStatus] = useState(null); // 'success' | 'error'
    const [modalMessage, setModalMessage] = useState('');
    const [passwordErrors, setPasswordErrors] = useState([]);
    const [fieldErrors, setFieldErrors] = useState({});

    // Get uid and token from URL parameters (Djoser format)
    const uid = searchParams.get('uid');
    const token = searchParams.get('token');

    useEffect(() => {
        if (!uid || !token) {
            setModalStatus('error');
            setModalMessage('Invalid or missing reset parameters. Please request a new password reset.');
            setModalOpen(true);
        }
    }, [uid, token]);

    const handleTogglePassword = () => {
        setShowPassword((prev) => !prev);
    };

    const handleToggleConfirmPassword = () => {
        setShowConfirmPassword((prev) => !prev);
    };

    const handleInputChange = (field) => (event) => {
        setFormData(prev => ({
            ...prev,
            [field]: event.target.value
        }));
        
        // Clear field errors when user starts typing
        if (fieldErrors[field]) {
            setFieldErrors(prev => ({
                ...prev,
                [field]: null
            }));
        }
    };

    const validatePassword = (password) => {
        const errors = [];
        if (password.length < 8) {
            errors.push('Password must be at least 8 characters long');
        }
        if (!/(?=.*[a-z])/.test(password)) {
            errors.push('Password must contain at least one lowercase letter');
        }
        if (!/(?=.*[A-Z])/.test(password)) {
            errors.push('Password must contain at least one uppercase letter');
        }
        if (!/(?=.*\d)/.test(password)) {
            errors.push('Password must contain at least one number');
        }
        if (!/(?=.*[@$!%*?&])/.test(password)) {
            errors.push('Password must contain at least one special character (@$!%*?&)');
        }
        return errors;
    };

    const handleResetPassword = async () => {
        // Clear previous errors
        setPasswordErrors([]);
        setFieldErrors({});

        // Validate required fields
        if (!formData.new_password || !formData.new_password_confirm) {
            setModalStatus('error');
            setModalMessage('Please fill in all fields.');
            setModalOpen(true);
            return;
        }

        // Validate password strength
        const passwordValidationErrors = validatePassword(formData.new_password);
        if (passwordValidationErrors.length > 0) {
            setPasswordErrors(passwordValidationErrors);
            return;
        }

        // Validate password confirmation
        if (formData.new_password !== formData.new_password_confirm) {
            setFieldErrors({
                new_password_confirm: 'Passwords do not match'
            });
            return;
        }

        if (!uid || !token) {
            setModalStatus('error');
            setModalMessage('Invalid or missing reset parameters.');
            setModalOpen(true);
            return;
        }

        setLoading(true);
        
        try {
            const response = await AuthService.passwordResetConfirm(uid, token, formData.new_password);
            
            setModalStatus('success');
            setModalMessage('Password reset successfully! You can now log in with your new password.');
            setModalOpen(true);
            
            // Clear the form
            setFormData({ new_password: '', new_password_confirm: '' });
            
        } catch (error) {
            console.error('Password reset error:', error);
            setModalStatus('error');
            if (isNetworkError(error)) {
                setModalMessage('Server unreachable. Is the backend running?');
            } else {
                setModalMessage(getSafeApiErrorMessage(error, 'Password reset failed. Please try again.'));
            }
            setModalOpen(true);
        } finally {
            setLoading(false);
        }
    };

    const handleCloseModal = () => {
        setModalOpen(false);
        if (modalStatus === 'success') {
            navigate('/login');
        }
    };

    const handleBackToLogin = () => {
        navigate('/login');
    };

    return (
        <Box
            sx={{
                minHeight: '100vh',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                py: 4
            }}
        >
            <Container maxWidth="sm">
                <Paper
                    elevation={10}
                    sx={{
                        p: 4,
                        borderRadius: 3,
                        background: 'rgba(255, 255, 255, 0.95)',
                        backdropFilter: 'blur(10px)'
                    }}
                >
                    {/* Header */}
                    <Box textAlign="center" mb={4}>
                        <Typography variant="h4" component="h1" gutterBottom sx={{ color: '#2c5aa0', fontWeight: 'bold' }}>
                            Reset Password
                        </Typography>
                        <Typography variant="body1" color="text.secondary">
                            Enter your new password below
                        </Typography>
                    </Box>

                    {/* Password Field */}
                    <TextField
                        type={showPassword ? "text" : "password"}
                        label="New Password"
                        fullWidth
                        value={formData.new_password}
                        onChange={handleInputChange('new_password')}
                        error={!!fieldErrors.new_password}
                        helperText={fieldErrors.new_password}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton
                                        onClick={handleTogglePassword}
                                        edge="end"
                                    >
                                        {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                        sx={{ mb: 3 }}
                    />

                    {/* Password Confirmation Field */}
                    <TextField
                        type={showConfirmPassword ? "text" : "password"}
                        label="Confirm New Password"
                        fullWidth
                        value={formData.new_password_confirm}
                        onChange={handleInputChange('new_password_confirm')}
                        error={!!fieldErrors.new_password_confirm}
                        helperText={fieldErrors.new_password_confirm}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton
                                        onClick={handleToggleConfirmPassword}
                                        edge="end"
                                    >
                                        {showConfirmPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                        sx={{ mb: 3 }}
                    />

                    {/* Password Strength Errors */}
                    {passwordErrors.length > 0 && (
                        <Alert severity="error" sx={{ mb: 3 }}>
                            <Typography variant="body2" component="div">
                                Password requirements:
                                <ul style={{ margin: '8px 0 0 0', paddingLeft: '20px' }}>
                                    {passwordErrors.map((error, index) => (
                                        <li key={index}>{error}</li>
                                    ))}
                                </ul>
                            </Typography>
                        </Alert>
                    )}

                    {/* Submit Button */}
                    <Button
                        variant="contained"
                        fullWidth
                        endIcon={<ArrowForwardIcon />}
                        onClick={handleResetPassword}
                        disabled={!formData.new_password || !formData.new_password_confirm || loading || passwordErrors.length > 0}
                        sx={{
                            py: 1.5,
                            backgroundColor: '#2D5EFF',
                            '&:hover': {
                                backgroundColor: '#1e4bd8'
                            },
                            mb: 2
                        }}
                    >
                        {loading ? 'Resetting Password...' : 'Reset Password'}
                    </Button>

                    {/* Back to Login Button */}
                    <Button
                        variant="outlined"
                        fullWidth
                        startIcon={<ArrowBackIcon />}
                        onClick={handleBackToLogin}
                        disabled={loading}
                        sx={{
                            py: 1.5,
                            borderColor: '#2D5EFF',
                            color: '#2D5EFF',
                            '&:hover': {
                                borderColor: '#1e4bd8',
                                backgroundColor: 'rgba(45, 94, 255, 0.04)'
                            }
                        }}
                    >
                        Back to Login
                    </Button>

                    {/* Success/Error Modal */}
                    <Dialog open={modalOpen} onClose={handleCloseModal} maxWidth="sm" fullWidth>
                        <DialogContent sx={{ pt: 3 }}>
                            <Box textAlign="center">
                                {modalStatus === 'success' ? (
                                    <CheckCircleIcon sx={{ fontSize: 60, color: 'success.main', mb: 2 }} />
                                ) : (
                                    <ErrorIcon sx={{ fontSize: 60, color: 'error.main', mb: 2 }} />
                                )}
                                <Typography variant="h6" gutterBottom>
                                    {modalStatus === 'success' ? 'Success!' : 'Error'}
                                </Typography>
                                <Typography variant="body1" color="text.secondary">
                                    {modalMessage}
                                </Typography>
                            </Box>
                        </DialogContent>
                        <DialogActions sx={{ justifyContent: 'center', pb: 3 }}>
                            <Button
                                onClick={handleCloseModal}
                                variant="contained"
                                sx={{
                                    backgroundColor: modalStatus === 'success' ? '#2D5EFF' : '#f44336',
                                    '&:hover': {
                                        backgroundColor: modalStatus === 'success' ? '#1e4bd8' : '#d32f2f'
                                    }
                                }}
                            >
                                {modalStatus === 'success' ? 'Continue to Login' : 'Try Again'}
                            </Button>
                        </DialogActions>
                    </Dialog>
                </Paper>
            </Container>
        </Box>
    );
};

export default PasswordResetConfirm;
